import type { OidcIssuerInfo } from 'models/identityProvider';
import type { OidcClientSettings } from 'oidc-client-ts';
import Config from 'services/Config/configService';

//
// Sign-in through OpenID Connect: Google Workspace, and the allow-listed issuers of organizations (a Nextcloud
// with its OIDC Identity Provider app, a Keycloak, an Okta). The browser talks to the issuer for the
// authorization request only: the token endpoint is the API's proxy (POST identity/google/token, respectively
// POST identity/oidc/token), which holds the client secret and keeps the refresh token, and renewals go through
// the API's renew endpoints with the previous id_token as proof. See isoplanner-api
// Core/Security/Docs/IdentityProviders.md, decisions 4 and 11.
//
// This folder is deliberately outside the folders copyFromWebClient.bat mirrors into the add-in hosts,
// which stay Entra-only and never install oidc-client-ts.
//

export const googleIssuer = 'https://accounts.google.com';

//the id_token scopes: no Drive or directory scope here, those are the admin's consent in Google Workspace
export const googleLoginScopes = 'openid email profile';

//what an organization's issuer is asked for: the standard profile claims, nothing provider specific
export const oidcLoginScopes = 'openid email profile';

export const googleClientId: string = Config.get('IdentityProviders.Google.ClientId');

//a stage without a client id has no Google button and no Google provider
export const isGoogleLoginEnabled = (): boolean => {
  return googleClientId !== '';
};

//a stage that switched the organization sign-in on; the issuers themselves are rows the API manages
export const isOidcLoginEnabled = (): boolean => {
  return Config.get('IdentityProviders.Oidc.Enabled') === 'true';
};

//the API base without a trailing slash: the production value carries one
export const getApiBaseUrl = (): string => {
  return (Config.get('Api.BaseURL') as string).replace(/\/+$/, '');
};

//the landing of every redirect: the root of the app, so the callback query is handled before routing
export const getOidcRedirectUri = (): string => {
  return Config.getAppURL() + '/';
};

//kept under its old name for the Google provider
export const getGoogleRedirectUri = getOidcRedirectUri;

export const getGoogleClientSettings = (): OidcClientSettings => {
  return {
    authority: googleIssuer,
    client_id: googleClientId,
    redirect_uri: getOidcRedirectUri(),
    response_type: 'code',
    scope: googleLoginScopes,
    //the metadata is fixed, so nothing is fetched at startup; the token endpoint is ours
    metadata: {
      issuer: googleIssuer,
      authorization_endpoint: 'https://accounts.google.com/o/oauth2/v2/auth',
      token_endpoint: `${getApiBaseUrl()}/identity/google/token`,
      jwks_uri: 'https://www.googleapis.com/oauth2/v3/certs',
      userinfo_endpoint: 'https://openidconnect.googleapis.com/v1/userinfo',
      revocation_endpoint: 'https://oauth2.googleapis.com/revoke',
    },
    //the profile comes from the id_token claims (sub, hd, email, name, picture); no userinfo round trip
    loadUserInfo: false,
    filterProtocolClaims: true,
  };
};

export const getOidcClientSettings = (info: OidcIssuerInfo): OidcClientSettings => {
  return {
    authority: info.issuer,
    client_id: info.clientId,
    redirect_uri: getOidcRedirectUri(),
    response_type: 'code',
    //offline_access only where the issuer lists it: the API then holds a refresh token and renews the session
    //itself; an issuer without it is renewed by one silent round trip through its own session
    scope: info.offlineAccessSupported ? `${oidcLoginScopes} offline_access` : oidcLoginScopes,
    //the issuer's endpoints come from the API (which read the discovery document), except the token endpoint,
    //which is the API's proxy; the issuer travels in the token request so the proxy knows whose secret to add
    metadata: {
      issuer: info.issuer,
      authorization_endpoint: info.authorizationEndpoint,
      token_endpoint: `${getApiBaseUrl()}/identity/oidc/token`,
      jwks_uri: info.jwksUri,
      end_session_endpoint: info.endSessionEndpoint,
      userinfo_endpoint: info.userInfoEndpoint,
    },
    extraTokenParams: { issuer: info.issuer },
    loadUserInfo: false,
    filterProtocolClaims: true,
  };
};
